import { z } from 'zod';
import { DATE_FORMATS, LANGUAGES, type DateFormat, type Language } from '../constants/locale';
import { CURRENCIES, type Currency } from '../money/money';

/** A point on the map: the company's own location, say. */
export const lngLatSchema = z.object({
  lng: z.number().min(-180).max(180),
  lat: z.number().min(-90).max(90),
});
export type LngLat = z.infer<typeof lngLatSchema>;

export const updateOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(80),
  legalName: z.string().trim().min(1).max(160).optional(),
  kraPin: z.string().trim().min(1).max(20).optional(),
  address: z.string().trim().min(1).max(500).optional(),
  /** Where the company is (its head office or main yard): shown on the Zones map. Omit to keep it; null clears it. */
  location: lngLatSchema.nullable().optional(),
  currency: z.enum(CURRENCIES).optional(),
  timezone: z.string().trim().min(1).max(80).optional(),
  dateFormat: z.enum(DATE_FORMATS).optional(),
  language: z.enum(LANGUAGES).optional(),
  /** How many days out a job's deadline counts as "due soon" (gitaumoses4/chukua#39) — read by the jobs list, not shown on the public profile. */
  jobDueSoonThresholdDays: z.number().int().min(1).max(365).optional(),
  /** How many days overdue a customer's invoices must be before the credit check at job confirmation treats them as "badly overdue" (gitaumoses4/chukua#37) — read by the credit-check seam, not shown on the public profile. */
  creditOverdueThresholdDays: z.number().int().min(1).max(365).optional(),
  /** Hours a driver rests after a trip ends before they count as available again (gitaumoses4/chukua#54). 0 means no rest period. */
  driverRestHours: z.number().int().min(0).max(72).optional(),
});
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

/** Default for a deployment that has never set `jobDueSoonThresholdDays` — the value the migration backfills too. */
export const DEFAULT_JOB_DUE_SOON_THRESHOLD_DAYS = 30;

/** Default for a deployment that has never set `creditOverdueThresholdDays` — the value the migration backfills too. Placeholder, not sourced from the PRD's ageing buckets — see gitaumoses4/chukua#37. */
export const DEFAULT_CREDIT_OVERDUE_THRESHOLD_DAYS = 60;

/** Default for a deployment that has never set `driverRestHours` — the value the migration backfills too. */
export const DEFAULT_DRIVER_REST_HOURS = 8;

/** What the web app shows for the company this deployment serves. Public: the login page needs it. */
export interface OrganizationProfile {
  /** False until the deployment has been given its company; the UI then shows Chukua's own branding. */
  configured: boolean;
  name: string;
  legalName: string | null;
  /** Same-origin, versioned (safe to cache forever). Null when no logo has been set. */
  logoUrl: string | null;
  /** Variant for dark surfaces; null means use `logoUrl` there too. */
  logoDarkUrl: string | null;
  currency: Currency;
  timezone: string;
  dateFormat: DateFormat;
  language: Language;
}

/** The admin-only settings screen: the public profile plus the company's tax registration and location. */
export interface OrganizationSettings extends OrganizationProfile {
  kraPin: string | null;
  address: string | null;
  /** Never on the public profile: the login page has no need of where the company is. */
  location: LngLat | null;
  jobDueSoonThresholdDays: number;
  creditOverdueThresholdDays: number;
  driverRestHours: number;
}

/** Before a deployment is given its company, both apps show Chukua's own branding and these defaults. */
export const UNCONFIGURED_ORGANIZATION: OrganizationProfile = {
  configured: false,
  name: 'Chukua',
  legalName: null,
  logoUrl: null,
  logoDarkUrl: null,
  currency: 'KES',
  timezone: 'Africa/Nairobi',
  dateFormat: 'DD/MM/YYYY',
  language: 'en',
};
